import { ref, computed, watch } from 'vue';
import { useAuth } from './useAuth.js';
import { useBudgetData } from './useBudgetData.js';
import { 
  createSavingsGoal,
  getSavingsGoals,
  updateSavingsGoal,
  deleteSavingsGoal,
  pauseSavingsGoal,
  resumeSavingsGoal,
  updateGoalProgress as apiUpdateGoalProgress,
  markGoalCompleted as apiMarkGoalCompleted
} from '../utils/savingsGoalsService.ts';
import {
  getGoalProgress,
  getGoalStatus,
  getGoalProgressPercentage,
  getGoalActiveDaysElapsed,
  getGoalRemainingDays,
  getGoalDailyAllocation,
  getGoalAdjustedEndDate,
  getGoalCalculatedProperties,
  daysBetween,
  addDays
} from '../utils/goalCalculations.js';

// Global savings goals state
const goals = ref([]);
const isLoading = ref(false);
const error = ref(null);
const hasAttemptedLoad = ref(false);

export function useSavingsGoals() {
  const { user, getAccessToken } = useAuth();
  const { transactions } = useBudgetData(); // Use transactions directly (now includes historical data)

  // Watch transactions for changes (completion is automatically reflected in computed properties)
  watch(transactions, () => {
    // No need to check for completions - they're dynamically calculated
    console.log('Transactions changed - goal statuses will update automatically via computed properties');
  }, { deep: true });

  // Computed properties for filtered goal lists
  const activeGoals = computed(() => 
    goals.value.filter(goal => getGoalStatus(goal, transactions.value) === 'active')
  );

  const completedGoals = computed(() => 
    goals.value.filter(goal => getGoalStatus(goal, transactions.value) === 'completed')
  );

  const pausedGoals = computed(() => 
    goals.value.filter(goal => getGoalStatus(goal, transactions.value) === 'paused')
  );

  // Calculate total daily allocation from all active goals
  const totalDailyAllocation = computed(() => 
    activeGoals.value.reduce((total, goal) => {
      return total + getGoalDailyAllocation(goal, transactions.value);
    }, 0)
  );

  // Calculate overall progress percentage across all active goals
  const activeGoalsProgressPercentage = computed(() => {
    if (activeGoals.value.length === 0) {
      return 0;
    }

    const totalTarget = activeGoals.value.reduce((sum, goal) => sum + goal.targetAmount, 0);
    const totalProgress = activeGoals.value.reduce((sum, goal) => sum + (getGoalProgress(goal.id, transactions.value) || 0), 0);

    if (totalTarget === 0) {
      return 0;
    }

    return (totalProgress / totalTarget) * 100;
  });

  // Validate goal data according to requirements
  const validateGoalData = (goalData) => {
    const errors = [];

    // Requirement 1.2: Goal name validation
    if (!goalData.name || typeof goalData.name !== 'string') {
      errors.push('Goal name is required');
    } else if (goalData.name.trim().length === 0) {
      errors.push('Goal name cannot be empty');
    } else if (goalData.name.length > 50) {
      errors.push('Goal name must be under 50 characters');
    }

    // Requirement 1.3: Target amount validation
    if (typeof goalData.targetAmount !== 'number' || goalData.targetAmount <= 0) {
      errors.push('Target amount must be a positive number greater than zero');
    }

    // Requirement 1.4: Duration validation
    if (!Number.isInteger(goalData.duration) || goalData.duration < 1 || goalData.duration > 365) {
      errors.push('Duration must be a positive integer between 1 and 365 days');
    }

    if (errors.length > 0) {
      throw new Error(errors.join('; '));
    }
  };

  // Create a new savings goal
  const createGoal = async (goalData) => {
    try {
      isLoading.value = true;
      error.value = null;

      // Validate input data
      validateGoalData(goalData);

      // Check if user is authenticated
      if (!user.value) {
        throw new Error('Authentication required to create goals');
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Create goal via API
      const apiResponse = await createSavingsGoal({
        name: goalData.name.trim(),
        targetAmount: goalData.targetAmount,
        duration: goalData.duration
      }, accessToken);

      // Transform API response to local format (pure data only)
      // Extract goal ID from SK (e.g., "GOAL#test-094497" -> "test-094497")
      const goalId = apiResponse.data.goal.SK.replace('GOAL#', '');
      
      const newGoal = {
        id: goalId, // Use extracted goal ID for API calls
        sk: apiResponse.data.goal.SK, // Keep full SK for reference
        name: apiResponse.data.goal.name,
        targetAmount: apiResponse.data.goal.targetAmount,
        duration: apiResponse.data.goal.duration,
        createdDate: apiResponse.data.goal.createdAt, // Map from createdAt
        status: apiResponse.data.goal.status,
        totalPausedDays: apiResponse.data.goal.totalPausedDays || 0,
        pausedDate: apiResponse.data.goal.pausedDate || null,
        resumedDate: apiResponse.data.goal.resumedDate || null
      };

      // Add to local state immediately (local-first approach)
      goals.value.push(newGoal);

      console.log('Goal created successfully:', newGoal);
      return newGoal;
    } catch (err) {
      error.value = err.message || 'Failed to create goal';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Update an existing goal
  const updateGoal = async (goalId, updates) => {
    try {
      isLoading.value = true;
      error.value = null;

      const goalIndex = goals.value.findIndex(g => g.id === goalId);
      if (goalIndex === -1) {
        throw new Error('Goal not found');
      }

      const currentGoal = goals.value[goalIndex];
      
      // Validate updates if they include core fields
      if (updates.name !== undefined || updates.targetAmount !== undefined || updates.duration !== undefined) {
        const validationData = {
          name: updates.name !== undefined ? updates.name : currentGoal.name,
          targetAmount: updates.targetAmount !== undefined ? updates.targetAmount : currentGoal.targetAmount,
          duration: updates.duration !== undefined ? updates.duration : currentGoal.duration
        };
        validateGoalData(validationData);
      }

      // Check if user is authenticated
      if (!user.value) {
        throw new Error('Authentication required to update goals');
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Update goal via API
      await updateSavingsGoal(goalId, updates, accessToken);

      // Apply updates to local state
      const updatedGoal = {
        ...currentGoal,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Update in local state
      goals.value[goalIndex] = updatedGoal;

      console.log('Goal updated successfully:', updatedGoal);
    } catch (err) {
      error.value = err.message || 'Failed to update goal';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Delete a goal
  const deleteGoal = async (goalId) => {
    try {
      isLoading.value = true;
      error.value = null;

      const goalIndex = goals.value.findIndex(g => g.id === goalId);
      if (goalIndex === -1) {
        throw new Error('Goal not found');
      }

      // Check if user is authenticated
      if (!user.value) {
        throw new Error('Authentication required to delete goals');
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Delete goal via API
      await deleteSavingsGoal(goalId, accessToken);

      // Remove from local state
      goals.value.splice(goalIndex, 1);

      console.log('Goal deleted successfully:', goalId);
    } catch (err) {
      error.value = err.message || 'Failed to delete goal';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Mark a goal as completed (deprecated - completion is now dynamically calculated)
  const markGoalCompleted = async (goalId) => {
    // Goals are automatically marked as completed when progress >= target
    // This function is kept for API compatibility but doesn't change stored status
    console.log(`Goal ${goalId} completion is now dynamically calculated based on transaction progress`);
  };

  // Pause a goal
  const pauseGoal = async (goalId) => {
    try {
      const currentGoal = goals.value.find(g => g.id === goalId);
      if (!currentGoal) {
        throw new Error('Goal not found');
      }

      const dynamicStatus = getGoalStatus(currentGoal, transactions.value);
      if (dynamicStatus !== 'active') {
        throw new Error('Only active goals can be paused');
      }

      // Check if user is authenticated
      if (!user.value) {
        throw new Error('Authentication required to pause goals');
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Pause goal via API
      await pauseSavingsGoal(goalId, accessToken);

      // Update local state
      await updateGoal(goalId, {
        status: 'paused',
        pausedDate: new Date().toISOString()
      });

      console.log(`Goal "${currentGoal.name}" paused successfully`);
    } catch (err) {
      error.value = err.message || 'Failed to pause goal';
      throw err;
    }
  };

  // Resume a paused goal
  const resumeGoal = async (goalId) => {
    try {
      const currentGoal = goals.value.find(g => g.id === goalId);
      if (!currentGoal) {
        throw new Error('Goal not found');
      }

      const dynamicStatus = getGoalStatus(currentGoal, transactions.value);
      if (dynamicStatus !== 'paused') {
        throw new Error('Goal is not paused');
      }

      // Check if user is authenticated
      if (!user.value) {
        throw new Error('Authentication required to resume goals');
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Resume goal via API
      await resumeSavingsGoal(goalId, accessToken);

      // Calculate days paused and update local state
      const daysPaused = currentGoal.pausedDate ? 
        daysBetween(currentGoal.pausedDate, new Date()) : 0;

      await updateGoal(goalId, {
        status: 'active',
        resumedDate: new Date().toISOString(),
        totalPausedDays: (currentGoal.totalPausedDays || 0) + daysPaused,
        pausedDate: null
      });

      console.log(`Goal "${currentGoal.name}" resumed successfully. Days paused: ${daysPaused}`);
    } catch (err) {
      error.value = err.message || 'Failed to resume goal';
      throw err;
    }
  };

  // Flag to prevent recursive updates (no longer needed but kept for compatibility)
  const isAutoUpdating = ref(false);

  // Automatic goal completion detection (removed - completion is now purely dynamic)
  const checkAndUpdateGoalCompletions = () => {
    // Goals are automatically shown as completed when progress >= target
    // No need to update stored status since completion is dynamically calculated
    console.log('Goal completion is now dynamically calculated - no status updates needed');
  };

  // Requirement 5.2, 5.3: Goal modification (completion status is dynamically calculated)
  const modifyGoal = async (goalId, modifications) => {
    const currentGoal = goals.value.find(g => g.id === goalId);
    if (!currentGoal) {
      throw new Error('Goal not found');
    }

    // Validate modifications if they include core fields
    if (modifications.name !== undefined || modifications.targetAmount !== undefined || modifications.duration !== undefined) {
      const validationData = {
        name: modifications.name !== undefined ? modifications.name : currentGoal.name,
        targetAmount: modifications.targetAmount !== undefined ? modifications.targetAmount : currentGoal.targetAmount,
        duration: modifications.duration !== undefined ? modifications.duration : currentGoal.duration
      };
      validateGoalData(validationData);
    }

    // Apply modifications - completion status will be recalculated automatically
    await updateGoal(goalId, modifications);
    
    console.log(`Goal "${currentGoal.name}" modified successfully. Status and allocations will update automatically.`);
  };

  // Update goal progress (completion status is dynamically calculated)
  const updateGoalProgress = async (goalId, newProgress) => {
    try {
      const currentGoal = goals.value.find(g => g.id === goalId);
      if (!currentGoal) {
        throw new Error('Goal not found');
      }

      if (newProgress < 0) {
        throw new Error('Progress cannot be negative');
      }

      // Check if user is authenticated
      if (!user.value) {
        throw new Error('Authentication required to update goal progress');
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Update progress via API
      await apiUpdateGoalProgress(goalId, newProgress, accessToken);

      // Update local state (completion status will be calculated dynamically)
      const updates = {
        actualProgress: newProgress
      };

      await updateGoal(goalId, updates);

      console.log('Goal progress updated:', { goalId, newProgress });
      console.log('Goal completion status will be calculated automatically based on progress vs target');
    } catch (err) {
      error.value = err.message || 'Failed to update goal progress';
      throw err;
    }
  };

  // Load goals from API (called by App.vue)
  const loadGoals = async () => {
    // Prevent multiple simultaneous loads
    if (isLoading.value) {
      console.log('Load already in progress, skipping...');
      return;
    }

    try {
      isLoading.value = true;
      error.value = null;
      hasAttemptedLoad.value = true;

      // Check if user is authenticated
      if (!user.value) {
        // If not authenticated, clear goals and return
        goals.value = [];
        console.log('User not authenticated, goals cleared');
        return;
      }

      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('Unable to authenticate. Please sign in again.');
      }

      // Load goals from API
      const apiResponse = await getSavingsGoals(accessToken);
      
      // Transform API response to local format (pure data only)
      const loadedGoals = apiResponse.data.goals.map(apiGoal => {
        // Ensure dates are properly formatted - API uses createdAt/updatedAt
        const createdDate = apiGoal.createdAt ? new Date(apiGoal.createdAt).toISOString() : new Date().toISOString();
        const pausedDate = apiGoal.pausedDate ? new Date(apiGoal.pausedDate).toISOString() : null;
        const resumedDate = apiGoal.resumedDate ? new Date(apiGoal.resumedDate).toISOString() : null;
        
        // Extract goal ID from SK (e.g., "GOAL#test-094497" -> "test-094497")
        const goalId = apiGoal.SK.replace('GOAL#', '');
        
        return {
          id: goalId, // Use extracted goal ID for API calls
          sk: apiGoal.SK, // Keep full SK for reference
          name: apiGoal.name,
          targetAmount: apiGoal.targetAmount,
          duration: apiGoal.duration,
          createdDate: createdDate, // Map from createdAt
          status: apiGoal.status,
          totalPausedDays: apiGoal.totalPausedDays || 0,
          pausedDate: pausedDate,
          resumedDate: resumedDate
        };
      });

      goals.value = loadedGoals;
      console.log('Goals loaded successfully from API:', goals.value.length);
    } catch (err) {
      error.value = err.message || 'Failed to load goals';
      console.error('Failed to load goals from API:', err);
      // Don't throw here - let the component handle the error state
    } finally {
      isLoading.value = false;
    }
  };

  // Clear goals when user signs out (called by App.vue)
  const clearGoals = () => {
    goals.value = [];
    hasAttemptedLoad.value = false;
    error.value = null;
    console.log('Goals cleared on sign out');
  };

  return {
    // Reactive State
    goals: computed(() => goals.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    
    // Computed Properties
    activeGoals,
    completedGoals,
    pausedGoals,
    totalDailyAllocation,
    activeGoalsProgressPercentage,
    
    // Methods
    createGoal,
    updateGoal,
    deleteGoal,
    loadGoals,
    clearGoals,
    markGoalCompleted,
    pauseGoal,
    resumeGoal,
    updateGoalProgress,
    modifyGoal,
    checkAndUpdateGoalCompletions,
    
    // Calculation functions (exposed for components to use)
    getGoalProgress: (goalId) => getGoalProgress(goalId, transactions.value),
    getGoalStatus: (goal) => getGoalStatus(goal, transactions.value),
    getGoalProgressPercentage: (goal) => getGoalProgressPercentage(goal, transactions.value),
    getGoalActiveDaysElapsed,
    getGoalRemainingDays,
    getGoalDailyAllocation: (goal) => getGoalDailyAllocation(goal, transactions.value),
    getGoalAdjustedEndDate,
    getGoalCalculatedProperties: (goal) => getGoalCalculatedProperties(goal, transactions.value)
  };
}